import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import LegalAcceptanceModal from '../components/dialogs/LegalAcceptanceModal';
import SampleCreationManager from '../components/sampleCreationManager/SampleCreationManager';
import UploadManager from '../components/uploadManager/UploadManager';
import { SampleCreationProvider } from '../contexts/SampleCreationContext';
import { UploadProvider } from '../contexts/UploadContext';
import { UserProvider, useUser } from '../contexts/UserContext';
import { setUserContext, startSessionReplayIfExternal } from '../utils/datadog';
import { identifyPendoUser } from '../utils/pendo';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    // Wait for the initial Auth0 + permissions load. Until this resolves the
    // route is pending.
    const resolved = await context.auth.whenReady();

    if (!resolved.isAuthenticated) {
      context.auth.login(location.href);
      // Keep the route pending while the redirect to login takes place.
      throw new Promise(() => {});
    }

    // Set the updated authentication state in the router context for child routes
    return {
      auth: {
        ...context.auth,
        isAuthenticated: resolved.isAuthenticated,
        hasPermission: resolved.hasPermission,
      },
    };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return (
    <UserProvider>
      <SampleCreationProvider>
        <UploadProvider>
          <OpenFeatureProvider suspendUntilReady={false}>
            <AuthenticatedContent />
          </OpenFeatureProvider>
        </UploadProvider>
      </SampleCreationProvider>
    </UserProvider>
  );
}

function AuthenticatedContent() {
  const { userData } = useUser();

  useEffect(() => {
    if (!userData) {
      return;
    }
    setUserContext({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role_name || 'user',
      organization: userData.org?.name,
      permissions: userData.permissions,
    });
    startSessionReplayIfExternal(userData.email);
    identifyPendoUser({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      roles: userData.roles,
      organization: userData.org
        ? { id: userData.org.id, name: userData.org.name }
        : undefined,
      permissions: userData.permissions,
    });
  }, [userData]);

  return (
    <>
      <LegalAcceptanceModal />
      <UploadManager />
      <SampleCreationManager />
      <Outlet />
    </>
  );
}
